import { useParams } from '@tanstack/react-router'
import { useAtom } from 'jotai'
import { useEffect, useState } from 'react'

import Body from '@/components/body/Body'
import { Box } from '@/components/ui/box'
import { Container } from '@/components/ui/container'
import { HEADER_HEIGHT } from '@/constants'
import { currentEnv } from '@/env/vars'
import useColorModeValue from '@/hooks/useColorModeValue'
import Chat from '@/pages/components/chat'
import LeftSide from '@/pages/components/translate/leftSide'
import {
    chatSettingsAtom,
    cleanData,
    conversationsListItemAtom,
    createChat,
    getChatSettings,
    getConversationsList,
    setConversation,
} from '@/store/gigiChat/ChatAtom'
import {
    getAllowedTranslationFileExtensions,
    getGlossariesInfo,
    getLanguages,
    glossariesInfoAtom,
    langsAtom,
    selectedLanguagesAtom,
    translateLeftSideExpandedAtom,
} from '@/store/translate'
import { IConversationSettings } from '@/types/gigiChat/ConversationSettings'

const TranslateContainer = () => {
    const [showSidebar] = useAtom(translateLeftSideExpandedAtom)
    const [chatSettings] = useAtom(chatSettingsAtom)
    const [langs] = useAtom(langsAtom)
    const [glossariesInfo] = useAtom(glossariesInfoAtom)
    const [, setSelectedLangs] = useAtom(selectedLanguagesAtom)
    const [conversations] = useAtom(conversationsListItemAtom)
    const [isLanguageSet, setIsLanguageSet] = useState<boolean>(false)
    // const [translationFileExtensions] = useAtom(translationFileExtensionsAtom)

    const params = useParams({ from: '/translate/$chatId' })

    const chatIdParam: string = params.chatId
    const getSettings = async (conversationId: string) => {
        const settings = (await getChatSettings(conversationId)) as IConversationSettings
        return settings
    }

    useEffect(() => {
        cleanData(true)
    }, [])

    // const allowedTranslationFileExtensionsLength = useMemo(() => {
    //     return translationFileExtensions?.length ?? 0
    // }, [translationFileExtensions])

    // useEffect(() => {
    //     if (!allowedTranslationFileExtensionsLength) {
    //         void getAllowedTranslationFileExtensions()
    //     }
    // }, [allowedTranslationFileExtensionsLength])

    //NOTE: please check that bellow things was really neccesary sice those wasn't working if mime types from documents exists :(
    useEffect(() => {
        void getAllowedTranslationFileExtensions()
    }, [])

    useEffect(() => {
        // ensure we only load once during the app life session
        // fast tab switch not covered
        if (!langs.length) {
            getLanguages()
        }
    }, [langs.length])

    useEffect(() => {
        if (!glossariesInfo.length) {
            currentEnv.features.isEnabled('glossaries') && getGlossariesInfo()
        }
    }, [glossariesInfo.length])

    useEffect(() => {
        const controller = new AbortController()
        const { signal } = controller
        void (async () => {
            await getConversationsList({ query: 'processing_strategy=Translation', isInitial: true, signal })
        })()

        return () => controller.abort('Another GET Conversations call was initiated')
    }, [])

    useEffect(() => {
        const controller = new AbortController()

        async function loadInitialData() {
            if (chatIdParam) {
                await getSettings(chatIdParam).then(async (res) => {
                    if (!controller.signal.aborted) {
                        res.processingStrategy = 'Translation'
                        setConversation(chatIdParam, res, 'Translation', controller.signal)
                    }
                })
            } else {
                await createChat('Translation', undefined, controller.signal)
            }
        }
        void loadInitialData()

        return () => {
            controller.abort('Another LOAD tranlsation initial data call was initiated')
        }
    }, [chatIdParam])

    useEffect(() => {
        // try to restore the language if we have languages and thre's no other
        if (!isLanguageSet && chatSettings && chatSettings.processingStrategy === 'Translation' && langs.length > 0) {
            if (chatIdParam && chatSettings.processingStrategyTranslation.languages.length > 0) {
                // restore from chat settings
                setSelectedLangs(langs.filter((l) => chatSettings.processingStrategyTranslation.languages.includes(l.languageCode)))
                setIsLanguageSet(true)
            }

            if (!chatIdParam && chatSettings.processingStrategyTranslation.languages.length === 0) {
                // new conversation detected - try to figure it out from conversations
                let languageCodes: string[] = []
                const translationsConversations = conversations.filter((c) => c.settings.processingStrategy === 'Translation')

                if (translationsConversations.length > 1) {
                    languageCodes = translationsConversations[1].settings.processingStrategyTranslation.languages
                } else {
                    languageCodes = chatSettings ? chatSettings.processingStrategyTranslation.languages : []
                }

                setSelectedLangs(langs.filter((language) => languageCodes.includes(language.languageCode)))
                setIsLanguageSet(true)
            }
        }
    }, [chatIdParam, chatSettings, conversations, isLanguageSet, langs, setSelectedLangs])

    return (
        <Container
            maxW="100%"
            h={[`calc(100% - ${HEADER_HEIGHT.mobile})`, `calc(100% - ${HEADER_HEIGHT.mobile})`, `calc(100% - ${HEADER_HEIGHT.desktop})`]}
            display="flex"
            flexDirection="column"
            flexGrow="1"
            padding="0"
        >
            <Body>
                <Box
                    maxWidth="400px"
                    width={showSidebar ? '100%' : '30px'}
                    mt="16px"
                    mr="16px"
                    height="calc(100% - 16px)"
                    borderRadius="0 8px"
                    boxShadow="0px 0px 8px 1px rgba(84, 79, 64, 0.06)"
                    borderLeft="0"
                    backgroundColor={useColorModeValue('white', 'dark.900')}
                    display={['none', 'none', 'none', 'block']}
                >
                    <LeftSide />
                </Box>
                <Box
                    flex={2}
                    position="relative"
                    mt="16px"
                    borderRadius={showSidebar ? '8px 0px 0px' : '8px 8px 0px 0px'}
                    boxShadow="0px 0px 8px 1px rgba(84, 79, 64, 0.06)"
                    borderRight="0"
                    backgroundColor={useColorModeValue('white', 'dark.900')}
                    mx={showSidebar ? '0' : 'auto'}
                    maxW={showSidebar ? ['100%', '100%', '100%', '100%'] : ['100%', '100%', '100%', '80%']}
                >
                    <div
                        style={{
                            position: 'absolute',
                            top: 0,
                            bottom: 0,
                            left: 0,
                            right: 0,
                            paddingLeft: '24px',
                            paddingRight: '24px',
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                    >
                        <Chat />
                    </div>
                </Box>
            </Body>
        </Container>
    )
}

export default TranslateContainer
